// Adapters: normalize live /api/platform/* payloads into the shape the
// existing prototype UI components expect. This is the single place where
// field-name / unit / format mismatches are reconciled. UI components are
// not touched.
//
// All adapters are defensive: any missing field returns a sensible default.
//
// ShelfLife responses are envelope-wrapped, e.g.
//   { ok, companies: [...], count }
//   { ok, stores: [...], count }
// Use unwrap(data, key) to pull the inner array/object safely.

export const unwrap = (data, key) => {
  if (!data) return null;
  if (Array.isArray(data)) return data; // already bare
  if (key && Object.prototype.hasOwnProperty.call(data, key)) return data[key];
  return data;
};

const safeArray = (v) => (Array.isArray(v) ? v : []);
const str = (v, fallback = "") => (typeof v === "string" ? v : fallback);
const num = (v, fallback = 0) => (typeof v === "number" && !Number.isNaN(v) ? v : fallback);

const relativeFromIso = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return str(iso, "—");
  const deltaSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.round(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.round(deltaSec / 3600)}h ago`;
  if (deltaSec < 86400 * 7) return `${Math.round(deltaSec / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const isoDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return str(iso, "—");
  return d.toISOString().slice(0, 10);
};

// Status normalization — accept either prototype keys or common live variants
const TENANT_STATUS_MAP = {
  active: "active",
  archived: "archived",
  onboarding: "onboarding",
  pending: "pending",
  suspended: "pending",
  churned: "pending",
  inactive: "pending",
};

const CATALOG_STATUS_MAP = {
  synced: "synced",
  complete: "synced",
  review: "review",
  in_review: "in_review",
  needs_review: "review",
  not_started: "not_started",
  not_imported: "not_started",
  failed: "review",
};

const BILLING_STATUS_MAP = {
  trial: "trial",
  paying: "active",
  active: "active",
  not_set: "not_set",
  none: "not_set",
  past_due: "review",
  canceled: "pending",
};

const mapEnum = (v, table, fallback) =>
  table[str(v).toLowerCase()] || fallback;

// ---------------- Companies / Clients dashboard ----------------
export const adaptCompanyCard = (c = {}, counts = {}) => {
  const id = str(c.id || c.company_id || c._id, "unknown");
  const storeCount = num(c.store_count ?? c.storeCount ?? counts.stores ?? (Array.isArray(c.stores) ? c.stores.length : 0));
  const userCount = num(c.user_count ?? c.userCount ?? counts.users_active ?? counts.users_total ?? (Array.isArray(c.users) ? c.users.length : 0));
  const catalogItems = num(c.catalog_items ?? c.catalogItems ?? counts.catalog_imports ?? c.catalog_item_count, 0);
  return {
    id,
    name: str(c.name || c.display_name || c.legal_name, "(unnamed)"),
    // Slug-style code. Used by lifecycle confirmations and the test-tenant
    // visual heuristic on the Clients dashboard. Empty string when upstream
    // omits it (rare but possible during the early provisioning window).
    code: str(c.code || c.slug, ""),
    status: mapEnum(c.status, TENANT_STATUS_MAP, "pending"),
    storeCount,
    userCount,
    // Do not synthesize a catalog status from item counts — that would lie
    // about what ShelfLife actually reports. Leave null when upstream
    // doesn't include it so the UI can render "Not reported".
    catalogStatus: c.catalog_status || c.catalogStatus
      ? mapEnum(c.catalog_status ?? c.catalogStatus, CATALOG_STATUS_MAP, null)
      : null,
    catalogItems,
    // Same policy for billing status — null if ShelfLife is silent.
    billingStatus: c.billing_status || c.billingStatus
      ? mapEnum(c.billing_status ?? c.billingStatus, BILLING_STATUS_MAP, null)
      : null,
    billingDays: num(c.billing_days_remaining ?? c.billingDays, 0),
    plan: str(c.plan || c.plan_name, "—"),
    renewal: isoDate(c.renewal_at || c.renewalAt || c.renewal_date),
    paymentMethod: str(c.payment_method || c.paymentMethod, "Not connected"),
    lastActivity: relativeFromIso(c.last_activity_at || c.lastActivityAt || c.updated_at || c.created_at),
    createdAt: isoDate(c.created_at || c.createdAt),
    headquarters: str(c.hq_location || c.headquarters || c.hq || c.slug, "—"),
    accountOwner: str(
      c.account_owner_name || c.accountOwner ||
        (c.account_owner && (c.account_owner.name || c.account_owner.email)),
      "—"
    ),
    notes: str(c.notes, ""),
  };
};

// Envelope-aware: handle {ok, company:{...}, counts:{...}} OR a bare company object.
export const adaptCompanyDetail = (payload = {}) => {
  const isEnvelope = payload && typeof payload === "object" && (payload.company || payload.counts);
  const c = isEnvelope ? (payload.company || {}) : payload;
  const counts = isEnvelope ? (payload.counts || {}) : {};
  const base = adaptCompanyCard(c, counts);
  return {
    ...base,
    legalName: str(c.legal_name || c.legalName, base.name),
    stores: safeArray(c.stores).map(adaptStore),
    departments: safeArray(c.departments).map((d) => (typeof d === "string" ? d : str(d.name, "(unnamed)"))),
    users: safeArray(c.users).map(adaptUser),
  };
};

// ---------------- Stores ----------------
export const adaptStore = (s = {}) => ({
  id: str(s.id || s.store_id, "unknown"),
  name: str(s.name || s.display_name, "(unnamed store)"),
  address: str(s.address || s.location || s.code, "—"),
  departments: num(s.department_count ?? s.departments_count ?? (Array.isArray(s.departments) ? s.departments.length : s.departments), 0),
  users: num(s.user_count ?? s.users_count ?? (Array.isArray(s.users) ? s.users.length : s.users), 0),
  status: s.active === false
    ? "pending"
    : s.active === true
    ? "active"
    : mapEnum(s.status, TENANT_STATUS_MAP, "active"),
});

// ---------------- Departments ----------------
export const adaptDepartments = (list) =>
  safeArray(list).map((d) => (typeof d === "string" ? d : str(d.name || d.label, "(unnamed)")));

// ---------------- Users ----------------
// User-status mapping. Deactivated / disabled users must NEVER be labelled
// "pending" — that label is reserved for users whom ShelfLife explicitly
// reports as pending/invited.
const USER_STATUS_MAP = {
  active: "active",
  enabled: "active",
  invited: "invited",
  pending: "invited",
  disabled: "inactive",
  deactivated: "inactive",
  inactive: "inactive",
};

export const adaptUser = (u = {}) => ({
  id: str(u.id || u.user_id, "unknown"),
  name: str(u.name || u.full_name || u.display_name, str(u.email, "(unnamed)")),
  email: str(u.email, "—"),
  role: str(u.role || u.tenant_role || (Array.isArray(u.roles) ? u.roles[0] : ""), "—"),
  // Phase 3D resilience: split store fields. `storeName` is the resolved name
  // when ShelfLife provides one; `storeId` is the raw id. Display code is
  // responsible for resolving id → name against the already-loaded stores
  // list and rendering "— (orphaned)" when the id cannot be resolved.
  storeName: u.store_name || u.storeName || (u.store && u.store.name) || null,
  storeId: u.store_id || (u.store && u.store.id) || null,
  // Forward-compat for an upcoming ShelfLife payload that includes
  // department_name on the user object. Not currently rendered.
  departmentName: u.department_name || u.departmentName || (u.department && u.department.name) || null,
  departmentId: u.department_id || (u.department && u.department.id) || null,
  // Strict truthful status. ShelfLife `active:false` → Inactive (never Pending).
  status:
    u.active === false ? "inactive" :
    u.active === true ? "active" :
    mapEnum(u.status, USER_STATUS_MAP, "active"),
  // Convenience boolean so the UI can split active vs inactive cleanly.
  isActive:
    u.active === false ? false :
    u.active === true ? true :
    !["disabled", "deactivated", "inactive"].includes(
      String(u.status || "").toLowerCase()
    ),
});

// ---------------- Action history ----------------

// Normalize raw upstream action strings into Control Center internal
// presentation categories. Recognized types get a tailored icon + label
// pair in OperatorActivity.jsx; unrecognized ones fall through to the
// generic "event" category, but still get a humanized typeLabel so the
// row never renders as raw JSON or a dotted machine identifier.
const ACTIVITY_TYPE_MAP = {
  // Legacy / non-namespaced upstream variants (Phase 2C era)
  company_created: "company_created",
  tenant_created: "company_created",
  catalog_imported: "catalog_imported",
  catalog_uploaded: "catalog_imported",
  user_created: "user_created",
  user_invited: "user_created",
  store_added: "store_added",
  store_created: "store_added",
  // Phase 3B — platform-scope tenant-draft lifecycle
  "platform.tenant_draft.create": "tenant_draft_created",
  "platform.tenant_draft.activated": "tenant_activated",
  "platform.company.activate": "tenant_activated",
  // Phase 3D — tenant lifecycle writes
  "platform.company.archive": "company_archived",
  "platform.company.unarchive": "company_unarchived",
  "platform.company.delete_test_tenant": "company_deleted",
  "platform.company.delete_test_tenant.dry_run": "company_delete_dry_run",
};

// Humanise an arbitrary action string into a display-safe label.
// Always returns a non-empty, non-JSON-looking string so unknown action
// types render safely.
//
//   "platform.tenant_draft.create"  -> "Tenant Draft Create"
//   "platform.company.archive"      -> "Company Archive"
//   "user_invited"                  -> "User Invited"
//   ""                              -> "Event"
const humanizeActionType = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "Event";
  const cleaned = s.replace(/^platform\./i, "").replace(/[._-]+/g, " ");
  return cleaned
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length > 1 && w === w.toUpperCase()
      ? w
      : w[0].toUpperCase() + w.slice(1).toLowerCase()))
    .join(" ") || "Event";
};

export const adaptActivity = (a = {}) => {
  const rawType = String(a.action_type || a.type || a.event_type || "").toLowerCase();
  const normalized = ACTIVITY_TYPE_MAP[rawType] || rawType || "event";
  return {
    id: str(a.id || a.event_id || a.action_id, Math.random().toString(36).slice(2, 10)),
    type: normalized,
    // Human-readable label used by the renderer when there's no tailored
    // LABELS[type] entry. Guaranteed non-empty + non-raw-JSON.
    typeLabel: humanizeActionType(a.action_type || a.type || a.event_type),
    actor: str(
      a.actor_name || a.actorName || a.actor_email ||
      (a.actor && (a.actor.name || a.actor.email)) ||
      a.user_name || a.actor_user_id, "—"
    ),
    target: str(
      a.target_name || a.targetName || (a.target && a.target.name) ||
      a.company_name ||
      (a.target_type ? `${a.target_type}${a.target_id ? `:${String(a.target_id).slice(0, 8)}` : ""}` : ""),
      "—"
    ),
    time: relativeFromIso(a.occurred_at || a.occurredAt || a.created_at || a.timestamp),
    description: str(a.description || a.message || (a.metadata ? JSON.stringify(a.metadata).slice(0, 140) : ""), ""),
    // Original upstream string preserved for debug/inspection. Not rendered.
    raw: rawType,
  };
};

// ---------------- Catalog imports ----------------
const MAPPING_STATUS_MAP = {
  complete: "complete",
  in_review: "in_review",
  needs_review: "in_review",
  not_started: "not_started",
  failed: "in_review",
};

export const adaptCatalogImport = (ci = {}) => {
  if (!ci || (typeof ci === "object" && Object.keys(ci).length === 0)) {
    return {
      file: null,
      mapping: { status: "not_started", percent: 0, mappedColumns: 0, totalColumns: 0 },
      departmentMappings: [],
      rowsTotal: 0,
      approvedRows: 0,
      rejectedRows: 0,
      warnings: [],
    };
  }
  const file = ci.file || ci.upload || null;
  const mapping = ci.mapping || ci.column_mapping || {};
  return {
    file: file
      ? {
          name: str(file.name || file.filename, "—"),
          type: str(file.type || file.file_type, "CSV"),
          size: file.size_bytes
            ? `${(file.size_bytes / 1024 / 1024).toFixed(1)} MB`
            : str(file.size, "—"),
          uploadedAt: relativeFromIso(file.uploaded_at || file.uploadedAt),
        }
      : null,
    mapping: {
      status: mapEnum(mapping.status, MAPPING_STATUS_MAP, "not_started"),
      percent: num(mapping.percent ?? mapping.completion_pct, 0),
      mappedColumns: num(mapping.mapped_columns ?? mapping.mappedColumns, 0),
      totalColumns: num(mapping.total_columns ?? mapping.totalColumns, 0),
    },
    departmentMappings: safeArray(ci.department_mappings || ci.departmentMappings).map((m) => ({
      source: str(m.source || m.source_code, "—"),
      target: str(m.target || m.target_name, "—"),
      items: num(m.items ?? m.item_count, 0),
      status: str(m.status, "mapped").toLowerCase() === "unmapped" ? "unmapped" : "mapped",
    })),
    rowsTotal: num(ci.rows_total ?? ci.rowsTotal, 0),
    approvedRows: num(ci.approved_rows ?? ci.approvedRows, 0),
    rejectedRows: num(ci.rejected_rows ?? ci.rejectedRows, 0),
    warnings: safeArray(ci.warnings).map((w) => ({
      code: str(w.code, "WARNING"),
      message: str(w.message, ""),
      count: num(w.count, 0),
    })),
  };
};

// pick the newest of a list of catalog imports
export const pickLatestCatalogImport = (list) => {
  const arr = safeArray(list);
  if (arr.length === 0) return null;
  const sorted = [...arr].sort((a, b) => {
    const at = a.created_at || a.uploaded_at || "";
    const bt = b.created_at || b.uploaded_at || "";
    return bt.localeCompare(at);
  });
  return sorted[0];
};

// ---------------- Feature flags ----------------
// ShelfLife returns { ok, flags: {key: bool, ...}, known: [keys] }.
// Convert to an array our table expects.
export const adaptFeatureFlag = (f = {}) => ({
  key: str(f.key || f.name, "unknown"),
  enabled: Boolean(f.enabled ?? f.is_enabled),
  scope: str(f.scope, "global"),
  description: str(f.description, ""),
  updatedAt: relativeFromIso(f.updated_at || f.updatedAt),
});

export const adaptFeatureFlagsList = (payload) => {
  if (!payload) return [];
  // Envelope form: { ok, flags: {key:bool,...}, known: [keys] }
  if (payload && typeof payload === "object" && payload.flags && typeof payload.flags === "object" && !Array.isArray(payload.flags)) {
    const known = Array.isArray(payload.known) ? payload.known : Object.keys(payload.flags);
    return known.map((k) => ({
      key: k,
      enabled: Boolean(payload.flags[k]),
      scope: "global",
      description: "",
      updatedAt: "—",
    }));
  }
  // Array form: [{key, enabled, ...}]
  if (Array.isArray(payload)) return payload.map(adaptFeatureFlag);
  if (Array.isArray(payload?.flags)) return payload.flags.map(adaptFeatureFlag);
  return [];
};

// ---------------- Health ----------------
// ShelfLife returns { ok, role, operator_id, operator_email, flag_enabled, server_time }
export const adaptHealth = (h = {}) => ({
  status: h.ok === true ? "ok" : str(h.status, "unknown").toLowerCase(),
  version: str(h.version, "—"),
  uptimeSeconds: num(h.uptime_seconds ?? h.uptimeSeconds, 0),
  dbConnected: Boolean(h.db_connected ?? h.dbConnected ?? h.ok ?? true),
  asOf: relativeFromIso(h.server_time || h.as_of || h.timestamp || new Date().toISOString()),
  role: str(h.role, ""),
  operatorEmail: str(h.operator_email, ""),
  flagEnabled: Boolean(h.flag_enabled),
});

// ---------------- Stats ----------------
// ShelfLife returns
//   { ok, company_id, inventory:{...}, removals:{...}, product_requests:{...},
//     notifications:{...}, users_active:N, stores:N, generated_at:ISO }
//
// The inner shapes of inventory / removals / product_requests / notifications
// are not strictly fixed upstream. We pass them through verbatim (minus
// nullish/empty values) so the UI can render whatever keys actually arrive.
const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const isMeaningful = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (Array.isArray(v)) return v.length > 0;
  if (isPlainObject(v)) return Object.keys(v).length > 0;
  return true;
};

// Return null when ShelfLife did not include the section at all (so the UI
// can render an explicit "not reported by ShelfLife" state instead of
// pretending a value exists).
const passthroughGroup = (raw) => {
  if (!isPlainObject(raw)) return null;
  const entries = Object.entries(raw).filter(([, v]) => isMeaningful(v));
  if (entries.length === 0) return null;
  return Object.fromEntries(entries);
};

export const adaptStats = (s = {}) => {
  const inv = isPlainObject(s.inventory) ? s.inventory : {};
  const rem = isPlainObject(s.removals) ? s.removals : {};
  const pr = isPlainObject(s.product_requests) ? s.product_requests : {};
  const notif = isPlainObject(s.notifications) ? s.notifications : {};

  // Inventory batches are NOT catalog items — they are individual
  // InventoryBatches records (active + historical/removed). Keep them
  // separate from any catalog product count so the UI can label them
  // truthfully.
  const inventoryBatchesTotal = (() => {
    const v = inv.total ?? inv.batches_total ?? inv.batchesTotal;
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  })();
  const inventoryBatchesActive = (() => {
    const v = inv.active ?? inv.batches_active ?? inv.batchesActive;
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  })();
  const inventoryBatchesHistorical =
    inventoryBatchesTotal !== null && inventoryBatchesActive !== null
      && inventoryBatchesTotal >= inventoryBatchesActive
      ? inventoryBatchesTotal - inventoryBatchesActive
      : null;

  // product_requests.pending = pending product requests / unknown-barcode
  // catalog requests. NOT pending user invites.
  const pendingProductRequests = (() => {
    const v = pr.pending ?? pr.open ?? pr.pending_count;
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  })();
  const productRequestsTotal = (() => {
    const v = pr.total ?? pr.total_count;
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  })();

  // Catalog product count must come from an explicit catalog field — we
  // intentionally do NOT fall back to inv.total because that is the
  // inventory-batches count, a different concept.
  const catalogProductCount = (() => {
    const v = s.catalog_item_count ?? s.catalog_items ?? s.catalogItemCount;
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  })();

  return {
    storeCount: num(s.stores ?? s.store_count, 0),
    userCount: num(s.users_active ?? s.user_count, 0),
    departmentCount: num(s.department_count ?? s.departmentCount, 0),
    // Catalog product count is null when ShelfLife is silent — the UI
    // surfaces "Not reported by ShelfLife" rather than fabricating a value
    // from inventory.total.
    catalogProductCount,
    activeStores: num(s.stores ?? s.active_stores, 0),
    // Truthful product-request fields (replace the old, misnamed
    // `pendingInvites` which actually held product_requests.pending).
    pendingProductRequests,
    productRequestsTotal,
    // Truthful inventory-batches fields.
    inventoryBatchesTotal,
    inventoryBatchesActive,
    inventoryBatchesHistorical,
    lastActivity: relativeFromIso(s.generated_at ?? s.last_activity_at),
    // Sectioned passthroughs — null when ShelfLife did not report the section.
    inventory: passthroughGroup(s.inventory),
    removals: passthroughGroup(s.removals),
    productRequests: passthroughGroup(s.product_requests),
    notifications: passthroughGroup(s.notifications),
    generatedAtIso: typeof s.generated_at === "string" ? s.generated_at : null,
    raw: s,
  };
};

// ---------------- Store coverage diagnostics ----------------
// ShelfLife returns
//   { ok, company_id, active_batches:N, active_workforce:N, samples:[{...}], generated_at }
// Convert samples → store rows the UI expects (best-effort).
export const adaptStoreCoverage = (c = {}) => {
  const samples = safeArray(c.samples);
  const stores = samples.map((s, i) => ({
    id: str(s.store_id || s.id || `sample-${i}`),
    name: str(s.store_name || s.name || s.label || `Sample ${i + 1}`, `Sample ${i + 1}`),
    departmentsCovered: num(s.departments_covered ?? s.covered ?? s.batches_active, 0),
    departmentsTotal: num(s.departments_total ?? s.total ?? s.batches_total, 0),
    coveragePct: num(s.coverage_pct ?? s.coverage, 0),
    missingDepartments: safeArray(s.missing_departments).map((d) => str(d.name || d, d)),
  }));
  // Synthesize an overall coverage figure if not provided.
  let overall = num(c.overall_coverage_pct ?? c.overallCoveragePct, 0);
  if (!overall && stores.length) {
    const totals = stores.reduce(
      (acc, s) => ({ cov: acc.cov + s.departmentsCovered, tot: acc.tot + s.departmentsTotal }),
      { cov: 0, tot: 0 }
    );
    overall = totals.tot ? Math.round((totals.cov / totals.tot) * 100) : 0;
  }
  return {
    stores,
    overallCoveragePct: overall,
    activeBatches: num(c.active_batches, 0),
    activeWorkforce: num(c.active_workforce, 0),
    generatedAt: relativeFromIso(c.generated_at),
    raw: c,
  };
};
